import { useState, useEffect, useRef } from "react";
import { Card, CardHead, SectionTitle, Badge, Dot } from "./theme";
import { useTheme } from "./theme";
import { GET } from "./api";
import { useLang } from "./i18n";

const sections = [
  { id: "quick", ko: "빠른 시작", en: "Quick Start" },
  { id: "images", ko: "VM 이미지", en: "VM Images" },
  { id: "ssh", ko: "SSH 접속", en: "SSH Access" },
  { id: "gpu", ko: "GPU 사용", en: "GPU Usage" },
  { id: "startstop", ko: "VM 시작/중지", en: "Start/Stop" },
  { id: "migration", ko: "서버 이동", en: "Migration" },
  { id: "collab", ko: "VM 공유", en: "Sharing" },
  { id: "ports", ko: "포트/서비스", en: "Ports" },
  { id: "requests", ko: "자원 신청", en: "Requests" },
  { id: "security", ko: "보안 안내", en: "Security" },
  { id: "faq", ko: "FAQ", en: "FAQ" },
];

export default function UserGuide({ me, servers, config, allGpus, setTab: parentSetTab }) {
  const { C } = useTheme();
  const { lang } = useLang();
  const [tab, setTab] = useState("servers");
  const [activeSection, setActiveSection] = useState("quick");
  const sectionRefs = useRef({});
  const ko = lang === "ko";
  const totalGpus = allGpus.length;
  const usedGpus = allGpus.filter(g => g.assigned).length;

  useEffect(() => {
    if (tab !== "guide") return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) setActiveSection(e.target.id);
      });
    }, { rootMargin: "-20% 0px -60% 0px" });
    Object.values(sectionRefs.current).forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [tab]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const Link = ({ to, children }) => (
    <span onClick={() => { if (typeof parentSetTab === "function") parentSetTab(to); }}
      style={{ color: C.cyan, cursor: "pointer", textDecoration: "underline", fontWeight: 700 }}>
      {children} →
    </span>
  );

  const B = ({ children }) => <span style={{ color: C.cyan, fontWeight: 700 }}>{children}</span>;
  const W = ({ children }) => <span style={{ color: C.amber, fontWeight: 700 }}>{children}</span>;
  const R = ({ children }) => <span style={{ color: C.red, fontWeight: 700 }}>{children}</span>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", gap: 6 }}>
        {[{k:"servers",l:ko?"서버 현황":"Servers"},{k:"guide",l:ko?"이용 가이드":"Guide"}].map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} style={{
            background: tab===t.k ? C.border2+"88" : "transparent",
            color: tab===t.k ? C.text : C.dim2,
            border: `1px solid ${tab===t.k ? C.border2 : "transparent"}`,
            borderRadius:6, padding:"5px 13px", fontSize:10, fontFamily:"monospace", fontWeight:600, cursor:"pointer",
          }}>{t.l}</button>
        ))}
      </div>

      {/* Server Status */}
      {tab === "servers" && (
        <>
          <SectionTitle title={ko ? "서버 현황" : "Server Status"} />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:10, marginBottom:16 }}>
            {[
              {l:ko?"서버":"Servers", v:(servers||[]).length, c:C.blue},
              {l:ko?"총 GPU":"Total GPUs", v:totalGpus, c:C.purple},
              {l:ko?"사용중":"In Use", v:usedGpus, c:C.amber},
              {l:ko?"여유":"Free", v:totalGpus-usedGpus, c:C.green},
            ].map(s=>(
              <Card key={s.l} style={{padding:"14px 16px", textAlign:"center"}}>
                <div style={{fontSize:9, color:C.dim2, letterSpacing:1, marginBottom:4}}>{s.l}</div>
                <div style={{fontSize:24, fontWeight:700, color:s.c, fontFamily:"'Rajdhani',sans-serif"}}>{s.v}</div>
              </Card>
            ))}
          </div>

          {(servers||[]).map(srv => {
            const sGpus = srv.gpus || [];
            const sUsed = sGpus.filter(g=>g.assigned).length;
            const srvHost = config?.servers?.[srv.id]?.host || config?.servers?.[srv.id]?.ip || srv.id;
            return (
              <Card key={srv.id} style={{marginBottom:12}}>
                <CardHead>
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    <Dot color={srv.online ? C.green : C.red} pulse={srv.online}/>
                    <span style={{fontFamily:"'Rajdhani',sans-serif", fontSize:14, fontWeight:700, color:C.text}}>
                      {srv.label || srv.id}
                    </span>
                    <Badge label={srv.online?"ONLINE":"OFFLINE"} color={srv.online?C.green:C.red}/>
                  </div>
                  <span style={{fontSize:10, color:C.dim2, fontFamily:"monospace"}}>{srvHost}</span>
                </CardHead>
                <div style={{padding:14}}>
                  <table style={{width:"100%", fontSize:11, fontFamily:"monospace", borderCollapse:"collapse"}}>
                    <tbody>
                      {[
                        {l:ko?"호스트":"Host", v:srvHost},
                        {l:"GPU", v:`${sGpus.length}x TITAN RTX (24GB VRAM)`},
                        {l:ko?"GPU 사용":"GPU Used", v:`${sUsed} / ${sGpus.length}`},
                      ].map(r=>(
                        <tr key={r.l} style={{borderBottom:`1px solid ${C.border}08`}}>
                          <td style={{padding:"6px 10px", color:C.dim2, width:120}}>{r.l}</td>
                          <td style={{padding:"6px 10px", color:C.text}}>{r.v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{display:"flex", gap:6, flexWrap:"wrap", marginTop:10}}>
                    {sGpus.map(g=>(
                      <div key={g.id} style={{
                        padding:"4px 10px", borderRadius:4, fontSize:10, fontFamily:"monospace",
                        background: g.assigned ? (g.assigned_user===me?.id ? C.cyan+"22" : C.amber+"18") : C.green+"18",
                        border:`1px solid ${g.assigned ? (g.assigned_user===me?.id ? C.cyan+"44" : C.amber+"44") : C.green+"44"}`,
                        color: g.assigned ? (g.assigned_user===me?.id ? C.cyan : C.amber) : C.green,
                      }}>
                        GPU {g.id}: {g.assigned ? g.assigned : (ko?"여유":"Free")}
                      </div>
                    ))}
                    {sGpus.length === 0 && <span style={{fontSize:10, color:C.dim2}}>{ko?"GPU 정보 로딩중...":"Loading GPU info..."}</span>}
                  </div>
                </div>
              </Card>
            );
          })}
        </>
      )}

      {/* User Guide with Sidebar */}
      {tab === "guide" && (
        <div style={{ display: "flex", gap: 20, position: "relative" }}>
          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <SectionTitle title={ko ? "NEOALI GPUS 이용 가이드" : "NEOALI GPUS User Guide"} />

            {/* Quick Start */}
            <div id="quick" ref={el => sectionRefs.current.quick = el}>
              <GuideCard C={C} title={ko ? "빠른 시작" : "Quick Start"}>
                {ko ? <>
                  <GLine C={C}>1. <Link to="requests">자원 신청</Link> 탭에서 새 VM을 신청합니다.</GLine>
                  <GLine C={C}>2. <B>GPU 필요 여부</B>를 선택하고, 서버/이미지/CPU/RAM을 설정합니다.</GLine>
                  <GLine C={C}>3. GPU 여유 + RAM 64GB 이하면 <B>자동으로 VM이 즉시 생성</B>됩니다.</GLine>
                  <GLine C={C}>4. <Link to="ssh">SSH 접속</Link> 탭에서 <B>접속 키를 다운로드</B>하고, SSH 명령어를 복사합니다.</GLine>
                  <GLine C={C}>5. <B>chmod 600 {'<VM이름>'}.pem</B> 후 SSH로 접속합니다.</GLine>
                </> : <>
                  <GLine C={C}>1. Go to <Link to="requests">Request</Link> tab and submit a new VM request.</GLine>
                  <GLine C={C}>2. Choose <B>GPU option</B>, server, image, CPU, RAM.</GLine>
                  <GLine C={C}>3. If GPU available + RAM ≤ 64GB, <B>VM is created instantly</B>.</GLine>
                  <GLine C={C}>4. Go to <Link to="ssh">SSH Access</Link> tab, <B>download key</B> and copy SSH command.</GLine>
                  <GLine C={C}>5. Run <B>chmod 600 {'<vm>'}.pem</B> then connect via SSH.</GLine>
                </>}
              </GuideCard>
            </div>

            {/* Images */}
            <div id="images" ref={el => sectionRefs.current.images = el}>
              <GuideCard C={C} title={ko ? "VM 이미지" : "VM Images"}>
                {ko ? <>
                  <GLine C={C}>• <B>Ubuntu 22.04 (Base)</B> — 기본 Ubuntu, SSH만 설치된 최소 이미지</GLine>
                  <GLine C={C}>• <B>CUDA 12.8 + Driver 570</B> — NVIDIA 드라이버 + CUDA 12.8 + cuDNN 포함</GLine>
                  <GLine C={C}>• <B>PyTorch + CUDA 12.8</B> — PyTorch, transformers, jupyter 등 ML 도구 포함</GLine>
                  <GLine C={C}><W>GPU 사용 시 반드시 cuda-base 또는 pytorch 이미지를 선택하세요.</W></GLine>
                </> : <>
                  <GLine C={C}>• <B>Ubuntu 22.04 (Base)</B> — Minimal Ubuntu with SSH only</GLine>
                  <GLine C={C}>• <B>CUDA 12.8 + Driver 570</B> — NVIDIA driver + CUDA 12.8 + cuDNN</GLine>
                  <GLine C={C}>• <B>PyTorch + CUDA 12.8</B> — PyTorch, transformers, jupyter, ML tools</GLine>
                  <GLine C={C}><W>You must select cuda-base or pytorch image to use GPU.</W></GLine>
                </>}
              </GuideCard>
            </div>

            {/* SSH */}
            <div id="ssh" ref={el => sectionRefs.current.ssh = el}>
              <GuideCard C={C} title={ko ? "SSH 접속 방법" : "SSH Access"}>
                {ko ? <>
                  <GLine C={C}>• 각 VM에는 <B>기본 SSH 키페어</B>가 자동 생성됩니다.</GLine>
                  <GLine C={C}>• <Link to="ssh">SSH 접속</Link> 탭에서 키를 다운로드(.pem)하세요.</GLine>
                  <GLine C={C}>• 키 권한 설정: <B>chmod 600 {'<VM이름>'}.pem</B></GLine>
                  <GLine C={C}>• 접속: <B>ssh -i {'<VM이름>'}.pem -p {'<포트>'} {'<사용자ID>'}@{'<서버>'}</B></GLine>
                  <GLine C={C}><R>비밀번호 접속은 비활성화되어 있습니다. 키 인증만 사용합니다.</R></GLine>
                  <GLine C={C}>• 자신의 공개키를 등록하면 <B>모든 VM에 자동 적용</B>됩니다.</GLine>
                  <GLine C={C}>• root 접속도 같은 키로 가능합니다.</GLine>
                </> : <>
                  <GLine C={C}>• Each VM has a <B>default SSH keypair</B> auto-generated.</GLine>
                  <GLine C={C}>• Download the key from <Link to="ssh">SSH Access</Link> tab.</GLine>
                  <GLine C={C}>• Set permissions: <B>chmod 600 {'<vm>'}.pem</B></GLine>
                  <GLine C={C}>• Connect: <B>ssh -i {'<vm>'}.pem -p {'<port>'} {'<user>'}@{'<host>'}</B></GLine>
                  <GLine C={C}><R>Password authentication is disabled. Key-only access.</R></GLine>
                  <GLine C={C}>• Register your public key to <B>auto-apply to all VMs</B>.</GLine>
                  <GLine C={C}>• Root access available with the same key.</GLine>
                </>}
              </GuideCard>
            </div>

            {/* GPU */}
            <div id="gpu" ref={el => sectionRefs.current.gpu = el}>
              <GuideCard C={C} title={ko ? "GPU 사용" : "GPU Usage"}>
                {ko ? <>
                  <GLine C={C}>• <Link to="gpustatus">GPU 현황</Link> 탭에서 전체 GPU 상태를 실시간으로 확인하세요.</GLine>
                  <GLine C={C}>• 내가 사용중인 GPU는 <B>'MY GPU'</B> 뱃지로 강조 표시됩니다.</GLine>
                  <GLine C={C}>• VM 시작 시 사용할 GPU를 직접 선택할 수 있습니다.</GLine>
                  <GLine C={C}><W>GPU 사용 시 반드시 이용 사유를 입력해주세요.</W></GLine>
                  <GLine C={C}><W>종료 예정을 입력하면 다른 사용자가 참고할 수 있습니다.</W></GLine>
                  <GLine C={C}>• VM을 중지하면 GPU가 <B>자동으로 해제</B>됩니다.</GLine>
                  <GLine C={C}><R>사용하지 않을 때는 반드시 VM을 중지하여 GPU를 반납해주세요.</R></GLine>
                </> : <>
                  <GLine C={C}>• Check real-time GPU status in <Link to="gpustatus">GPU Status</Link> tab.</GLine>
                  <GLine C={C}>• Your GPUs are highlighted with <B>'MY GPU'</B> badge.</GLine>
                  <GLine C={C}>• Select GPUs when starting a VM.</GLine>
                  <GLine C={C}><W>Please enter a usage reason when using GPU.</W></GLine>
                  <GLine C={C}><W>Entering expected end time helps other users.</W></GLine>
                  <GLine C={C}>• GPU is <B>automatically released</B> when VM is stopped.</GLine>
                  <GLine C={C}><R>Please stop your VM when not in use to free GPUs.</R></GLine>
                </>}
              </GuideCard>
            </div>

            {/* Start/Stop */}
            <div id="startstop" ref={el => sectionRefs.current.startstop = el}>
              <GuideCard C={C} title={ko ? "VM 시작 / 중지" : "VM Start / Stop"}>
                {ko ? <>
                  <GLine C={C}>• <Link to="myvms">내 VM</Link> 탭에서 시작/중지할 수 있습니다.</GLine>
                  <GLine C={C}>• 시작 시 <B>GPU 선택 화면</B>이 표시됩니다.</GLine>
                  <GLine C={C}>• GPU 없이도 시작 가능합니다 (CPU 전용 작업).</GLine>
                  <GLine C={C}>• 이전보다 GPU 수가 줄면 확인 메시지가 표시됩니다.</GLine>
                  <GLine C={C}>• <B>중지 시 데이터는 보존</B>됩니다. 안심하세요.</GLine>
                </> : <>
                  <GLine C={C}>• Start/stop from <Link to="myvms">My VMs</Link> tab.</GLine>
                  <GLine C={C}>• <B>GPU selection screen</B> appears when starting.</GLine>
                  <GLine C={C}>• Can start without GPU (CPU-only work).</GLine>
                  <GLine C={C}>• Confirmation appears if GPU count decreases.</GLine>
                  <GLine C={C}>• <B>Data is preserved</B> when stopped.</GLine>
                </>}
              </GuideCard>
            </div>

            {/* Migration */}
            <div id="migration" ref={el => sectionRefs.current.migration = el}>
              <GuideCard C={C} title={ko ? "서버 이동 (복제)" : "Server Migration (Clone)"}>
                {ko ? <>
                  <GLine C={C}>• 현재 서버에 GPU가 부족하면 <B>관리자에게 VM 복제를 요청</B>하세요.</GLine>
                  <GLine C={C}>• 관리자가 VM을 다른 서버로 복제하면 환경이 그대로 복사됩니다.</GLine>
                  <GLine C={C}>• 설치한 패키지, 설정, 데이터가 <B>모두 유지</B>됩니다.</GLine>
                  <GLine C={C}>• 복제 시간은 VM 크기에 따라 <B>1~5분</B> 소요됩니다.</GLine>
                  <GLine C={C}>• 복제된 VM은 <B>새 SSH 포트</B>가 할당됩니다.</GLine>
                  <GLine C={C}><W>원본 VM은 유지됩니다. 불필요하면 관리자에게 삭제 요청하세요.</W></GLine>
                </> : <>
                  <GLine C={C}>• If GPUs are full, <B>ask admin to clone your VM</B> to another server.</GLine>
                  <GLine C={C}>• Admin clones your VM — environment is copied as-is.</GLine>
                  <GLine C={C}>• All packages, settings, data are <B>preserved</B>.</GLine>
                  <GLine C={C}>• Clone takes <B>1-5 minutes</B> depending on size.</GLine>
                  <GLine C={C}>• Cloned VM gets a <B>new SSH port</B>.</GLine>
                  <GLine C={C}><W>Original VM is preserved. Ask admin to delete if not needed.</W></GLine>
                </>}
              </GuideCard>
            </div>

            {/* Collaborators */}
            <div id="collab" ref={el => sectionRefs.current.collab = el}>
              <GuideCard C={C} title={ko ? "VM 공유 (협업자)" : "VM Sharing"}>
                {ko ? <>
                  <GLine C={C}>• <Link to="myvms">내 VM</Link> 탭에서 협업자를 추가할 수 있습니다.</GLine>
                  <GLine C={C}>• 협업자는 <B>자신의 SSH 키</B>로 VM에 접속합니다.</GLine>
                  <GLine C={C}>• VM 내에 <B>별도 리눅스 계정</B>이 생성됩니다.</GLine>
                  <GLine C={C}>• 협업자도 <B>sudo 권한</B>을 가집니다.</GLine>
                  <GLine C={C}>• 소유자가 협업자를 제거하면 해당 계정이 삭제됩니다.</GLine>
                </> : <>
                  <GLine C={C}>• Add collaborators from <Link to="myvms">My VMs</Link> tab.</GLine>
                  <GLine C={C}>• Collaborators SSH with <B>their own keys</B>.</GLine>
                  <GLine C={C}>• A <B>separate Linux account</B> is created per collaborator.</GLine>
                  <GLine C={C}>• Collaborators have <B>sudo privileges</B>.</GLine>
                  <GLine C={C}>• Removing a collaborator deletes their account.</GLine>
                </>}
              </GuideCard>
            </div>

            {/* Ports */}
            <div id="ports" ref={el => sectionRefs.current.ports = el}>
              <GuideCard C={C} title={ko ? "포트 / 서비스" : "Ports / Services"}>
                {ko ? <>
                  <GLine C={C}>• 각 VM에 <B>서비스 포트 범위</B>가 할당됩니다 (50개).</GLine>
                  <GLine C={C}>• <Link to="services">포트/서비스</Link> 탭에서 서비스 포트를 추가하세요.</GLine>
                  <GLine C={C}>• 예: Jupyter(8888), TensorBoard(6006) 등</GLine>
                  <GLine C={C}>• <B>'외부'</B> 접근: 호스트포트로 직접 접속 가능</GLine>
                  <GLine C={C}>• <B>'터널'</B> 접근: SSH 터널로만 접속</GLine>
                  <GLine C={C}>• SSH 터널: <B>ssh -L 8888:localhost:8888 -i key.pem -p {'<포트>'} user@host</B></GLine>
                </> : <>
                  <GLine C={C}>• Each VM gets a <B>service port range</B> (50 ports).</GLine>
                  <GLine C={C}>• Add ports from <Link to="services">Ports/Services</Link> tab.</GLine>
                  <GLine C={C}>• Example: Jupyter(8888), TensorBoard(6006)</GLine>
                  <GLine C={C}>• <B>'External'</B>: direct connection via host port</GLine>
                  <GLine C={C}>• <B>'Tunnel'</B>: SSH tunnel only</GLine>
                  <GLine C={C}>• Tunnel: <B>ssh -L 8888:localhost:8888 -i key.pem -p {'<port>'} user@host</B></GLine>
                </>}
              </GuideCard>
            </div>

            {/* Requests */}
            <div id="requests" ref={el => sectionRefs.current.requests = el}>
              <GuideCard C={C} title={ko ? "자원 신청" : "Resource Requests"}>
                {ko ? <>
                  <GLine C={C}>• <Link to="requests">자원 신청</Link> 탭에서 새 VM 또는 자원 변경을 신청합니다.</GLine>
                  <GLine C={C}>• <B>GPU 여유 + RAM 64GB 이하</B> → 자동 승인 (즉시 생성)</GLine>
                  <GLine C={C}>• <W>GPU 부족 또는 RAM 64GB 초과</W> → 관리자 승인 필요</GLine>
                  <GLine C={C}><W>신청 시 사유를 반드시 입력해주세요.</W></GLine>
                </> : <>
                  <GLine C={C}>• Submit requests from <Link to="requests">Request</Link> tab.</GLine>
                  <GLine C={C}>• <B>GPU available + RAM ≤ 64GB</B> → auto-approved (instant)</GLine>
                  <GLine C={C}>• <W>GPU shortage or RAM {'>'} 64GB</W> → admin approval needed</GLine>
                  <GLine C={C}><W>Please provide a reason for your request.</W></GLine>
                </>}
              </GuideCard>
            </div>

            {/* Security */}
            <div id="security" ref={el => sectionRefs.current.security = el}>
              <GuideCard C={C} title={ko ? "보안 안내" : "Security"}>
                {ko ? <>
                  <GLine C={C}><R>VM에서 외부 네트워크 접속이 제한되어 있습니다.</R></GLine>
                  <GLine C={C}>• 허용: GitHub, PyPI, Hugging Face, NVIDIA, PyTorch, 학내 네트워크</GLine>
                  <GLine C={C}>• <R>허용되지 않은 외부 접속은 차단되며 로그가 기록됩니다.</R></GLine>
                  <GLine C={C}><W>SSH 키를 안전하게 보관하세요. 타인과 공유하지 마세요.</W></GLine>
                  <GLine C={C}>• 필요한 사이트가 차단된 경우 관리자에게 문의하세요.</GLine>
                </> : <>
                  <GLine C={C}><R>Outbound network access from VMs is restricted.</R></GLine>
                  <GLine C={C}>• Allowed: GitHub, PyPI, Hugging Face, NVIDIA, PyTorch, campus network</GLine>
                  <GLine C={C}>• <R>Unauthorized connections are blocked and logged.</R></GLine>
                  <GLine C={C}><W>Keep SSH keys secure. Do not share with others.</W></GLine>
                  <GLine C={C}>• Contact admin if a required site is blocked.</GLine>
                </>}
              </GuideCard>
            </div>

            {/* FAQ */}
            <div id="faq" ref={el => sectionRefs.current.faq = el}>
              <GuideCard C={C} title="FAQ">
                {ko ? <>
                  <GQ C={C}>Q: VM에 접속이 안 됩니다.</GQ>
                  <GA C={C}>SSH 키 권한을 확인하세요 (<B>chmod 600</B>). <Link to="ssh">SSH 접속</Link> 탭에서 키를 다시 다운로드하세요.</GA>
                  <GQ C={C}>Q: GPU가 인식되지 않습니다.</GQ>
                  <GA C={C}><B>cuda-base</B> 또는 <B>pytorch</B> 이미지로 VM을 생성했는지 확인하세요. ubuntu-base에는 드라이버가 없습니다.</GA>
                  <GQ C={C}>Q: pip install이 안 됩니다.</GQ>
                  <GA C={C}>보안 정책으로 일부 CDN이 차단될 수 있습니다. 관리자에게 문의하세요.</GA>
                  <GQ C={C}>Q: VM 디스크가 부족합니다.</GQ>
                  <GA C={C}>관리자에게 <B>디스크 마운트</B>를 요청하세요. 호스트 디스크를 NFS로 연결할 수 있습니다.</GA>
                  <GQ C={C}>Q: 다른 서버의 GPU를 쓰고 싶습니다.</GQ>
                  <GA C={C}>관리자에게 <B>VM 복제</B>를 요청하세요. 환경이 그대로 복사됩니다.</GA>
                  <GQ C={C}>Q: 접속에 필요한 사이트가 차단됩니다.</GQ>
                  <GA C={C}>관리자에게 해당 도메인의 <B>방화벽 허용</B>을 요청하세요.</GA>
                </> : <>
                  <GQ C={C}>Q: I can't connect to my VM.</GQ>
                  <GA C={C}>Check key permissions (<B>chmod 600</B>). Re-download from <Link to="ssh">SSH Access</Link> tab.</GA>
                  <GQ C={C}>Q: GPU is not detected.</GQ>
                  <GA C={C}>Make sure you used <B>cuda-base</B> or <B>pytorch</B> image. ubuntu-base has no drivers.</GA>
                  <GQ C={C}>Q: pip install doesn't work.</GQ>
                  <GA C={C}>Some CDNs may be blocked by security policy. Contact admin.</GA>
                  <GQ C={C}>Q: VM disk is full.</GQ>
                  <GA C={C}>Ask admin for a <B>disk mount</B>. Host disk can be connected via NFS.</GA>
                  <GQ C={C}>Q: I want GPUs on another server.</GQ>
                  <GA C={C}>Ask admin to <B>clone your VM</B>. Environment is copied as-is.</GA>
                  <GQ C={C}>Q: A required site is blocked.</GQ>
                  <GA C={C}>Ask admin to <B>whitelist the domain</B> in the firewall.</GA>
                </>}
              </GuideCard>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ width: 140, flexShrink: 0, position: "sticky", top: 80, alignSelf: "flex-start", height: "fit-content" }}>
            <div style={{ background: C.panel + "cc", borderRadius: 8, border: `1px solid ${C.border}`, padding: "10px 0" }}>
              {sections.map(s => (
                <div key={s.id} onClick={() => scrollTo(s.id)} style={{
                  padding: "5px 14px", fontSize: 10, fontFamily: "monospace", cursor: "pointer",
                  color: activeSection === s.id ? C.cyan : C.dim2,
                  fontWeight: activeSection === s.id ? 700 : 400,
                  borderLeft: activeSection === s.id ? `2px solid ${C.cyan}` : "2px solid transparent",
                  transition: "all 0.2s",
                }}>
                  {ko ? s.ko : s.en}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GuideCard({ C, title, children }) {
  return (
    <Card style={{ marginBottom: 12 }}>
      <CardHead>
        <span style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: 1 }}>
          {title}
        </span>
      </CardHead>
      <div style={{ padding: "12px 16px" }}>{children}</div>
    </Card>
  );
}

function GLine({ C, children }) {
  return <div style={{ fontSize: 12, color: C.text, fontFamily: "monospace", lineHeight: 2 }}>{children}</div>;
}
function GQ({ C, children }) {
  return <div style={{ fontSize: 12, color: C.cyan, fontFamily: "monospace", lineHeight: 2, fontWeight: 700, marginTop: 8 }}>{children}</div>;
}
function GA({ C, children }) {
  return <div style={{ fontSize: 12, color: C.text, fontFamily: "monospace", lineHeight: 2, paddingLeft: 16, marginBottom: 4 }}>{children}</div>;
}
